import { Dropdown } from '@/components/Dropdown/Dropdown';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { ThreeDotsCircle } from '@/components/ThreeDotsCircle';
import SquareTopDown from '@/images/arrows/SquareTopDown.svg';
import { useAnalytics } from '@/services/analytics';
import { MixPanelEvent } from '@/utils/types';
import { LibraryItem } from '@d-id/types';
import styled from '@emotion/styled';
import { MenuItem } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { getSimpleShowMenuActions, SimpleShowMenuOptions } from './simpleshow-utils';
import { MenuDateText, MenuOptionsButtonWrapper, MenuTitleContainer, StyledMenuDivider, StyledMenuList } from './videoTileMenuStyles';

const VideoTitle = styled.div`
    font-weight: 700;
    font-size: 20px;
    letter-spacing: 0.05rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: ${({ theme }) => theme.colors.secondary[900]};
    line-height: normal;
`;

const StyledMenuItem = styled(MenuItem)`
    display: flex;
    padding: 8px 12px;
    justify-content: start;
    align-items: center;
    gap: 6px;
    margin: 0;
    font-family: Manrope;
    font-size: 1.4rem;
    font-style: normal;
    line-height: normal;
    font-weight: 600;
    letter-spacing: 0em;
`;

const dateFormatOptions: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };

interface Props {
    video: LibraryItem;
    menuOptions: SimpleShowMenuOptions;
    onOpen?: () => void;
    onClose?: () => void;
    origin: string;
}

export const SimpleShowVideoOptionsList = ({ video, menuOptions, onOpen, onClose, origin }: Props) => {
    const mixpanel = useAnalytics();
    const [closed, setClosed] = useState<boolean>(true);

    const handleOpenMenu = () => {
        setClosed(false);
        onOpen?.();
    };

    const handleCloseMenu = useCallback(() => {
        setClosed(true);
        onClose?.();
    }, [onClose]);

    const openUrl = useCallback(
        (url: string, eventName: 'video-edit' | 'video-preview') => {
            mixpanel?.track(eventName, {
                event: MixPanelEvent.CLICK,
                from: origin,
                videoId: video.id,
                editorType: 'video-maker',
            });
            window.open(url, '_blank', 'noopener,noreferrer');
            handleCloseMenu();
        },
        [mixpanel, origin, video.id, handleCloseMenu]
    );

    const createMenuItem = useCallback(
        (action: { text: string; url: string; eventName: 'video-edit' | 'video-preview' }) => ({
            label: (
                <StyledMenuItem
                    onClick={e => {
                        e.stopPropagation();
                        openUrl(action.url, action.eventName);
                    }}>
                    {action.text}
                    <MaskedIcon src={SquareTopDown} size="16px" />
                </StyledMenuItem>
            ),
        }),
        [openUrl]
    );

    const menuItems = useMemo(() => {
        const actions = getSimpleShowMenuActions(menuOptions);
        return actions.map(createMenuItem);
    }, [menuOptions, createMenuItem]);

    const lastEditedDate = video.metadata?.lastModified || video.created_at;
    const formattedDate = new Date(lastEditedDate).toLocaleDateString('en-US', dateFormatOptions);

    return (
        <Dropdown
            closed={closed}
            onOpen={handleOpenMenu}
            onClose={handleCloseMenu}
            trigger={
                <MenuOptionsButtonWrapper gap={6}>
                    <ThreeDotsCircle data-testid={`simpleshow-options-three-dots-${video.name}`} isActive={!closed} />
                </MenuOptionsButtonWrapper>
            }
            items={menuItems}
            renderMenuWrapper={children => (
                <StyledMenuList>
                    <MenuTitleContainer>
                        <VideoTitle>{video.name}</VideoTitle>
                    </MenuTitleContainer>
                    <MenuDateText>Last edited {formattedDate}</MenuDateText>
                    <StyledMenuDivider />
                    {children}
                </StyledMenuList>
            )}
        />
    );
};
